import { produce } from 'immer';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ProductStatusTypes } from '../../../../../../common/enums';
import {
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import { ErrorPanel } from '../../../../../../common/widgets/ErrorPanel';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { ErrorState, ErrorType } from '../../../../../benefit/models/Errors';
import {
  CodeSourceTypes,
  ProductActionTypes,
  ProductTypes,
  SoCodeTypes,
} from '../../../../enums';
import { rewriteImageUrl } from '../../../../helpers';
import { CountyFilterModel } from '../../../../models/filters';
import {
  EditGiftCardReadModel,
  EditPercentageVoucherReadModel,
  EditProductCommand,
  EditProductOrServiceReadModel,
  EditValueVoucherReadModel,
  ProductStatusModel,
  ProductTypeModel,
} from '../../../../models/products';
import { resources } from '../../../../resources/resources';
import { filtersService } from '../../../../services/filters';
import { productsService } from '../../../../services/products';
import {
  descriptionField,
  getBarcodeTypeOptions,
  howToOrderField,
  nameField,
  ProductContext,
} from '../constants';
import {
  GiftCardOfferDetails,
  GiftCardOfferDetailsForm,
} from '../OfferDetails/GiftCard';
import {
  PercentageVoucherOfferDetails,
  PercentageVoucherOfferDetailsForm,
} from '../OfferDetails/PercentageVoucher';
import {
  ProductOrServiceOfferDetails,
  ProductOrServiceOfferDetailsForm,
} from '../OfferDetails/ProductOrService';
import {
  ValueVoucherOfferDetails,
  ValueVoucherOfferDetailsForm,
} from '../OfferDetails/ValueVoucher';
import { ProductDetails } from '../ProductDetails';
import { ProductPreview } from '../ProductPreview';
import { Translations } from '../Translations';
import {
  ProductDetailsForm,
  getAddCodesCommand,
  getEnField,
  getEnName,
  getGiftCardOfferDetails,
  getLocationFilterIds,
  getPercentageVoucherOfferDetails,
  getProductOrServiceOfferDetails,
  getRoField,
  getRoName,
  getValueVoucherOfferDetails,
  validate,
} from '../utils';

import ModalCheckImg from '../../../../../../common/assets/images/merchants/modal-check.svg';
import { HttpResponse } from '../../../../../../common/ajax';

enum EditProductTabs {
  Details,
  Offer,
  Translations,
}

interface EditProductProps {
  onlyAddCodes: boolean;
}

export const EditProduct = (props: EditProductProps) => {
  const { onlyAddCodes } = props;
  const [productTypes, setProductTypes] = useState<ProductTypeModel[]>([]);
  const [productStatuses, setProductStatuses] = useState<ProductStatusModel[]>(
    [],
  );
  const [countiesWithCities, setCountiesWithCities] = useState<
    CountyFilterModel[]
  >([]);
  const [detailsForm, setDetailsForm] = useState<ProductDetailsForm>({
    productDescriptions: {
      ro: {
        name: { name: getRoName(nameField) },
        description: { name: getRoName(descriptionField) },
        howToOrder: { name: getRoName(howToOrderField) },
      },
      en: {
        name: { name: getEnName(nameField) },
        description: { name: getEnName(descriptionField) },
        howToOrder: { name: getEnName(howToOrderField) },
      },
    },
  });
  const [
    productOrServiceOfferDetailsForm,
    setProductOrServiceOfferDetailsForm,
  ] = useState<ProductOrServiceOfferDetailsForm>({
    codes: { generated: [], manuallyAdded: { valid: [], invalid: [] } },
    isCourse: false,
  });
  const [giftCardOfferDetailsForm, setGiftCardOfferDetailsForm] =
    useState<GiftCardOfferDetailsForm>({
      codes: { generated: [], manuallyAdded: { valid: [], invalid: [] } },
      orderInfo: false,
    });
  const [valueVoucherOfferDetailsForm, setValueVoucherOfferDetailsForm] =
    useState<ValueVoucherOfferDetailsForm>({});
  const [
    percentageVoucherOfferDetailsForm,
    setPercentageVoucherOfferDetailsForm,
  ] = useState<PercentageVoucherOfferDetailsForm>({});
  const [loadings, setLoadings] = useState<{
    general: boolean;
    sendForReview: boolean;
    saveChanges: boolean;
    saveCodes: boolean;
  }>({
    general: true,
    sendForReview: false,
    saveChanges: false,
    saveCodes: false,
  });
  const [codeSourceType, setCodeSourceType] = useState<
    CodeSourceTypes | undefined
  >();
  const [soCodeType, setSoCodeType] = useState<SoCodeTypes | undefined>();
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [productType, setProductType] = useState<ProductTypes>();
  const [productStatus, setProductStatus] = useState<ProductStatusTypes>();
  const [activeTab, setActiveTab] = useState<EditProductTabs>(
    onlyAddCodes ? EditProductTabs.Offer : EditProductTabs.Details,
  );
  const [showSentForReviewDialog, setShowSentForReviewDialog] =
    useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { productId } = useParams();

  const numberProductId =
    !!productId && !!_.parseInt(productId) ? _.parseInt(productId) : undefined;

  const loadData = async (abortController?: AbortController) => {
    if (!productId || !_.parseInt(productId)) {
      setShowErrorPanel(true);
    }
    setLoadings(
      produce(d => {
        d.general = true;
      }),
    );
    const resp = await productsService.getEditProductReadModel(
      _.parseInt(productId!),
      {
        signal: abortController?.signal,
      },
    );
    if (abortController?.signal.aborted) return;
    setLoadings(
      produce(d => {
        d.general = false;
      }),
    );

    if (resp.hasErrors) {
      setShowErrorPanel(true);
    } else {
      setDetailsForm({
        image: resp.data.image,
        productDescriptions: {
          ro: {
            name: getRoField(resp.data.roDescription.name, nameField),
            description: getRoField(
              resp.data.roDescription.description,
              descriptionField,
            ),
            howToOrder: getRoField(
              resp.data.roDescription.howToOrder,
              howToOrderField,
            ),
          },
          en: {
            name: getEnField(resp.data.enDescription.name, nameField),
            description: getEnField(
              resp.data.enDescription.description,
              descriptionField,
            ),
            howToOrder: getEnField(
              resp.data.enDescription.howToOrder,
              howToOrderField,
            ),
          },
        },
        locationType: resp.data.locationType,
        selectedCountiesWithCities: resp.data.selectedCountiesWithCities,
      });
      setProductType(resp.data.typeId);
      setProductStatus(resp.data.statusId);
      if (resp.data.typeId === ProductTypes.ProductOrService) {
        const data = resp.data as EditProductOrServiceReadModel;
        setProductOrServiceOfferDetailsForm({
          defaultPrice: data.defaultPrice,
          discountedPrice: data.discountedPrice,
          codes: { generated: [], manuallyAdded: { valid: [], invalid: [] } },
          readonlyCodes: data.codes,
          isCourse: data.isCourse,
          barcodeType: getBarcodeTypeOptions(translate).find(
            e => e.id === data.barcodeType,
          ),
        });
        setCodeSourceType(data.codeSourceTypeId);
        setSoCodeType(data.soCodeType);
      }
      if (resp.data.typeId === ProductTypes.GiftCard) {
        const data = resp.data as EditGiftCardReadModel;
        setGiftCardOfferDetailsForm({
          defaultPrice: data.defaultPrice,
          discountedPrice: data.discountedPrice,
          codes: { generated: [], manuallyAdded: { valid: [], invalid: [] } },
          readonlyCodes: data.codes,
          orderInfo: data.orderInfo,
          emails: data.emails,
          barcodeType: getBarcodeTypeOptions(translate).find(
            e => e.id === data.barcodeType,
          ),
        });
        setCodeSourceType(data.codeSourceTypeId);
        setSoCodeType(data.soCodeType);
      }
      if (resp.data.typeId === ProductTypes.ValueVoucher) {
        const data = resp.data as EditValueVoucherReadModel;
        setValueVoucherOfferDetailsForm({
          customVoucherCode: data.customVoucherCode,
          customVoucherDiscount: data.customVoucherDiscount,
          customVoucherUrl: data.customVoucherUrl,
        });
      }
      if (resp.data.typeId === ProductTypes.PercentageVoucher) {
        const data = resp.data as EditPercentageVoucherReadModel;
        setPercentageVoucherOfferDetailsForm({
          customVoucherDiscount: data.customVoucherDiscount,
          voucherDescription: data.voucherDescription,
          codeType: data.codeType,
          randomCode: data.randomCode,
          barcode: data.barcode,
        });
      }
    }
  };

  useMount(abortController => {
    const loadProductTypes = async (abortController?: AbortController) => {
      const resp = await productsService.getProductTypes({
        signal: abortController?.signal,
      });
      if (abortController?.signal.aborted) return;
      if (resp.hasErrors) {
        setShowErrorPanel(true);
      } else {
        setProductTypes(resp.data);
      }
    };
    const loadProductStatuses = async (abortController?: AbortController) => {
      const resp = await productsService.getProductStatuses({
        signal: abortController?.signal,
      });
      if (abortController?.signal.aborted) return;
      if (resp.hasErrors) {
        setShowErrorPanel(true);
      } else {
        setProductStatuses(resp.data);
      }
    };
    const loadCountiesWithCities = async (
      abortController?: AbortController,
    ) => {
      const resp = await filtersService.getCountiesWithCities({
        signal: abortController?.signal,
      });
      if (abortController?.signal.aborted) return;
      if (resp.hasErrors) {
        setShowErrorPanel(true);
      } else {
        setCountiesWithCities(resp.data);
      }
    };
    loadProductTypes();
    loadProductStatuses();
    loadCountiesWithCities();
    loadData(abortController);
  });

  const onSaveCodesClick = async () => {
    setLoadings(
      produce(d => {
        d.saveCodes = true;
      }),
    );

    const getForm = () => {
      if (productType === ProductTypes.GiftCard)
        return giftCardOfferDetailsForm;
      if (productType === ProductTypes.ProductOrService)
        return productOrServiceOfferDetailsForm;
    };

    const data = getAddCodesCommand(
      _.parseInt(productId!),
      getForm()!,
      codeSourceType,
    );

    const resp = await productsService.addCodes(data);

    setLoadings(
      produce(d => {
        d.saveCodes = false;
      }),
    );

    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      showSuccessToast(translate(resources.codes.add.success));
      navigate(-1);
    }
  };

  const isValidAction = (productActionType: ProductActionTypes): boolean => {
    if (productActionType === ProductActionTypes.SendForReview) {
      const { isValid, errors } = validate(
        detailsForm,
        ProductContext.Edit,
        productType!,
        productOrServiceOfferDetailsForm,
        giftCardOfferDetailsForm,
        percentageVoucherOfferDetailsForm,
        valueVoucherOfferDetailsForm,
      );
      if (!isValid) {
        showErrorToast(
          translate(resources.products.add.validation.errorsInForm),
        );
        setErrors(errors);
        return false;
      }
    }

    return true;
  };

  const callEditProduct = async (
    productActionType: ProductActionTypes,
  ): Promise<HttpResponse<boolean>> => {
    const data: EditProductCommand = {
      productId: _.parseInt(productId!),
      productActionType,
      productDetails: {
        image: detailsForm.image,
        filterIds: getLocationFilterIds(detailsForm),
        roDescription: detailsForm.productDescriptions.ro,
        enDescription: detailsForm.productDescriptions.en,
      },
      giftCardOfferDetails:
        productType === ProductTypes.GiftCard
          ? getGiftCardOfferDetails(giftCardOfferDetailsForm, codeSourceType)
          : undefined,
      productOrServiceOfferDetails:
        productType === ProductTypes.ProductOrService
          ? getProductOrServiceOfferDetails(
              productOrServiceOfferDetailsForm,
              codeSourceType,
            )
          : undefined,
      valueVoucherOfferDetails:
        productType === ProductTypes.ValueVoucher
          ? getValueVoucherOfferDetails(valueVoucherOfferDetailsForm)
          : undefined,
      percentageVoucherOfferDetails:
        productType === ProductTypes.PercentageVoucher
          ? getPercentageVoucherOfferDetails(percentageVoucherOfferDetailsForm)
          : undefined,
    };

    return productsService.editProduct(data);
  };

  const onSaveClick = async (productActionType: ProductActionTypes) => {
    if (!isValidAction(productActionType)) return;

    setLoadings(
      produce(d => {
        if (productActionType === ProductActionTypes.SendForReview) {
          d.sendForReview = true;
        } else if (productActionType === ProductActionTypes.SaveAsDraft) {
          d.saveChanges = true;
        }
      }),
    );

    const resp = await callEditProduct(productActionType);

    setLoadings(
      produce(d => {
        d.sendForReview = false;
        d.saveChanges = false;
      }),
    );

    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      if (productActionType === ProductActionTypes.SendForReview) {
        setShowSentForReviewDialog(true);
      } else {
        if (productActionType === ProductActionTypes.SaveAsDraft) {
          showSuccessToast(
            translate(resources.products.edit.confirmationToast.title),
          );
          navigate('/products');
        }
      }
    }
  };

  if (loadings.general) return <LoadingSpinner />;

  if (showErrorPanel) return <ErrorPanel />;

  return (
    <>
      <ProductPreview
        productTypes={productTypes}
        producyType={productType!}
        productStatuses={productStatuses}
        status={productStatus!}
        name={detailsForm.productDescriptions.ro.name.value}
        imageContent={
          detailsForm.imageContent ?? rewriteImageUrl(detailsForm.image)
        }
        defaultPrice={
          productOrServiceOfferDetailsForm.defaultPrice ??
          giftCardOfferDetailsForm.defaultPrice
        }
        discountedPrice={
          productOrServiceOfferDetailsForm.discountedPrice ??
          giftCardOfferDetailsForm.discountedPrice
        }
        showPricePreview={
          productType === ProductTypes.ProductOrService ||
          productType === ProductTypes.GiftCard
        }
      />

      <div className="m-panel edit-product-panel">
        {!onlyAddCodes && (
          <EditProductTabsStrip
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === EditProductTabs.Details && (
          <ProductDetails
            productId={numberProductId}
            countiesWithCities={countiesWithCities!}
            detailsForm={detailsForm}
            errors={errors}
            setErrors={setErrors}
            setDetailsForm={setDetailsForm}
            showContactDetailsFields={false}
          />
        )}
        {activeTab === EditProductTabs.Offer &&
          productType === ProductTypes.ProductOrService && (
            <ProductOrServiceOfferDetails
              productId={numberProductId}
              productName={detailsForm.productDescriptions.ro.name.value}
              offerDetailsForm={productOrServiceOfferDetailsForm}
              setOfferDetailsForm={setProductOrServiceOfferDetailsForm}
              codeSourceType={codeSourceType}
              soCodeType={soCodeType}
              errors={errors}
              setErrors={setErrors}
              onlyAddCodes={onlyAddCodes}
              context={ProductContext.Edit}
            />
          )}
        {activeTab === EditProductTabs.Offer &&
          productType === ProductTypes.GiftCard && (
            <GiftCardOfferDetails
              productId={numberProductId}
              productName={detailsForm.productDescriptions.ro.name.value}
              offerDetailsForm={giftCardOfferDetailsForm}
              setOfferDetailsForm={setGiftCardOfferDetailsForm}
              codeSourceType={codeSourceType}
              soCodeType={soCodeType}
              errors={errors}
              setErrors={setErrors}
              onlyAddCodes={onlyAddCodes}
              context={ProductContext.Edit}
            />
          )}
        {activeTab === EditProductTabs.Offer &&
          productType === ProductTypes.ValueVoucher && (
            <ValueVoucherOfferDetails
              offerDetailsForm={valueVoucherOfferDetailsForm}
              setOfferDetailsForm={setValueVoucherOfferDetailsForm}
              errors={errors}
              setErrors={setErrors}
            />
          )}
        {activeTab === EditProductTabs.Offer &&
          productType === ProductTypes.PercentageVoucher && (
            <PercentageVoucherOfferDetails
              offerDetailsForm={percentageVoucherOfferDetailsForm}
              setOfferDetailsForm={setPercentageVoucherOfferDetailsForm}
              errors={errors}
              setErrors={setErrors}
            />
          )}
        {activeTab === EditProductTabs.Translations && (
          <Translations
            detailsForm={detailsForm}
            setDetailsForm={setDetailsForm}
            errors={errors}
            setErrors={setErrors}
            showContactDetailsFields={false}
          />
        )}
      </div>
      <div className="m-actions-panel edit-product-actions">
        {!onlyAddCodes ? (
          <>
            <Button
              className="btn btn-outline"
              onClick={() => onSaveClick(ProductActionTypes.SendForReview)}
              loading={loadings.sendForReview}
              disabled={loadings.saveChanges}
            >
              <span className="btn-text">
                {translate(resources.sendForReview)}
              </span>
            </Button>
            <Button
              className="btn btn-solid"
              onClick={() => onSaveClick(ProductActionTypes.SaveAsDraft)}
              loading={loadings.saveChanges}
              disabled={loadings.sendForReview}
            >
              <span className="btn-text">
                {translate(resources.saveChanges)}
              </span>
            </Button>
          </>
        ) : (
          <>
            <Button
              className="btn btn-outline"
              onClick={() => navigate(-1)}
              disabled={loadings.saveCodes}
            >
              <span className="btn-text">{translate(resources.back)}</span>
            </Button>
            <Button
              className="btn btn-solid"
              onClick={onSaveCodesClick}
              loading={loadings.saveCodes}
            >
              <span className="btn-text">
                {translate(resources.codes.add.save)}
              </span>
            </Button>
          </>
        )}
      </div>
      {showSentForReviewDialog && (
        <SentForReviewDialog closeDialog={() => navigate('/products')} />
      )}
    </>
  );
};

const EditProductTabsStrip = (props: {
  activeTab: EditProductTabs;
  setActiveTab: (tab: EditProductTabs) => void;
}) => {
  const { activeTab, setActiveTab } = props;
  const { t: translate } = useTranslation();
  return (
    <ul className="merchants-panel-tabs">
      <li
        className={
          activeTab === EditProductTabs.Details ? 'merchants-tab-active' : ''
        }
        onClick={() => setActiveTab(EditProductTabs.Details)}
      >
        {translate(resources.products.edit.tabs.details)}
      </li>
      <li
        className={
          activeTab === EditProductTabs.Offer ? 'merchants-tab-active' : ''
        }
        onClick={() => setActiveTab(EditProductTabs.Offer)}
      >
        {translate(resources.products.edit.tabs.offer)}
      </li>
      <li
        className={
          activeTab === EditProductTabs.Translations
            ? 'merchants-tab-active'
            : ''
        }
        onClick={() => setActiveTab(EditProductTabs.Translations)}
      >
        {translate(resources.products.edit.tabs.translations)}
      </li>
    </ul>
  );
};

const SentForReviewDialog = (props: { closeDialog: () => void }) => {
  const { closeDialog } = props;
  const { t: translate } = useTranslation();

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      closeOnClickOutside
      className="dialog-s"
    >
      <div className="dialog-content">
        <div className="dialog-image m-dialog-img">
          <img
            src={ModalCheckImg}
            alt={translate(resources.products.edit.sendForReviewDialog.title)}
          />
        </div>
        <div className="dialog-title">
          {translate(resources.products.edit.sendForReviewDialog.title)}
        </div>
        <div className="dialog-text centered">
          {translate(resources.products.edit.sendForReviewDialog.body)}
        </div>
      </div>
      <div className="dialog-actions vertical">
        <Button onClick={closeDialog} className="btn btn-solid">
          <span className="btn-text">{translate(resources.understood)}</span>
        </Button>
      </div>
    </Dialog>
  );
};
